export const formatDate = (date: number): string => {
  const theDate = new Date(date)
  const year = theDate.getFullYear()
  const month = 1 + theDate.getMonth()
  const day = theDate.getDate()
  return `${year}.${makeTwoDigit(month)}.${makeTwoDigit(day)}`
}

const makeTwoDigit = (date: number): string => {
  return date.toString().padStart(2, '0')
}

/**
 * 지원하는 로케일 타입 정의 (예: 'en-US', 'ko-KR', 'ja-JP')
 */

export enum SupportedLocale {
  'EN_US' = 'en-US',
  'KO_KR' = 'ko-KR',
  'JA_JP' = 'ja-JP',
  'TH_TH' = 'th-TH',
  'ZH_HANS_CN' = 'zh-Hans-CN',
  'ZH_HANT_TW' = 'zh-Hant-TW',
  'EN' = 'en',
  'KO' = 'ko',
  'JA' = 'ja',
  'TH' = 'th',
  'CN' = 'cn',
  'TW' = 'tw',
}

interface IsCurrentTimeInRangeRange {
  from?: string
  to?: string
  timezone?: string // 예: 'Asia/Seoul', 'America/New_York'
}

/**
 * 현재 시간이 시간 범위를 확인하는 함수입니다. 지정된 타임존의 현재 시간을 기준으로 합니다.
 * @param from - 시작 시간 (형식: YYYY-MM-DD HH:mm)
 * @param to - 종료 시간 (형식: YYYY-MM-DD HH:mm, 옵션)
 * @param timezone - 타임존 (예: 'Asia/Seoul', 기본값: 'UTC')
 * @returns 현재 시간이 지정된 범위에 포함되는지 여부
 */
export const isCurrentTimeInRange = ({ from, to, timezone }: IsCurrentTimeInRangeRange): boolean => {
  timezone = timezone || getCurrentTimezone()

  if (!from && !to) {
    throw new Error('from 또는 to 중 하나는 필수로 입력해야 합니다.')
  }

  // 현재 시간을 지정된 타임존으로 변환
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }))

  // from과 to 시간을 지정된 타임존으로 변환
  if (from) {
    const fromTime = new Date(new Date(from).toLocaleString('en-US', { timeZone: timezone }))

    if (Number.isNaN(fromTime.getTime())) {
      console.error("'from'은 'YYYY-MM-DD HH:mm' 형식이어야 합니다.")
      return false
    }
    if (now < fromTime) {
      return false
    }
  }

  if (to) {
    const toTime = new Date(new Date(to).toLocaleString('en-US', { timeZone: timezone }))

    if (Number.isNaN(toTime.getTime())) {
      console.error("'to'는 'YYYY-MM-DD HH:mm' 형식이어야 합니다.")
      return false
    }
    if (now > toTime) {
      return false
    }
  }
  return true
}

/**
 * 브라우저의 현재 타임존을 반환하는 함수입니다.
 * @returns IANA 타임존 문자열 (예: 'Asia/Seoul')
 */
export const getCurrentTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * 현재 시간이 지정된 시간 이후인지 확인하는 함수입니다.
 * @param time - 기준 시간 (형식: YYYY-MM-DD HH:mm)
 * @param timezone - 타임존 (예: 'Asia/Seoul', 기본값: 'Asia/Seoul')
 * @returns 현재 시간이 지정된 시간 이후인지 여부
 *
 * @example 11/17 오후 12시 이후인지 확인하는 예시
 *
 * if (isAfterTime('2025-11-17 12:00')) {
 *  2번 이미지 표시
 * } else {
 *  1번 이미지 표시
 * }
 */
export const isAfterTime = (time: string, timezone = 'Asia/Seoul'): boolean => {
  try {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }))
    const targetTime = new Date(new Date(time).toLocaleString('en-US', { timeZone: timezone }))

    if (Number.isNaN(targetTime.getTime())) {
      console.error(`'time'은 'YYYY-MM-DD HH:mm' 형식이어야 합니다. 입력값: ${time}`)
      return false
    }

    return now >= targetTime
  } catch (error) {
    console.error('isAfterTime 에러:', error)
    return false
  }
}

/**
 * 현재 시간이 지정된 시간 이전인지 확인하는 함수입니다.
 * @param time - 기준 시간 (형식: YYYY-MM-DD HH:mm)
 * @param timezone - 타임존 (예: 'Asia/Seoul', 기본값: 'Asia/Seoul')
 * @returns 현재 시간이 지정된 시간 이전인지 여부
 *
 * @example 11/17 오후 12시 이전인지 확인하는 예시
 *
 * if (isBeforeTime('2025-11-17 12:00')) {
 *   1번 이미지 표시
 * } else {
 *  2번 이미지 표시
 * }
 */
export const isBeforeTime = (time: string, timezone = 'Asia/Seoul'): boolean => {
  return !isAfterTime(time, timezone)
}
